import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { useEramanUserProfile } from "../Contents.hook";
import { fields } from "../constants";

export const UserProfile = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();

  const {
    data: editData,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchEditData,
  } = useEramanUserProfile();

  const {
    data: dataById,
    isLoading: isLoadingData,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    } else {
      refetchEditData();
    }
  }, [query.get("id")]);

  const AwardsFormConfig: FormConfig = {
    initialValues: {
      field_name_text: decodeHTML(
        dataById ? dataById.payload.field_name_text : editData ? editData?.[0]?.field_name_text : ""
      ),
      field_name_text_ms: decodeHTML(
        dataById ? dataById.payload.field_name_text_ms : editData ? editData?.[0].field_name_text_ms : ""
      ),
      field_name_text_zh: decodeHTML(
        dataById ? dataById.payload.field_name_text_zh : editData ? editData?.[0]?.field_name_text_zh : ""
      ),
      title: decodeHTML(dataById ? dataById.payload.title : editData ? editData?.[0]?.title : ""),
      field_username_text: decodeHTML(
        dataById ? dataById.payload.field_username_text : editData ? editData?.[0]?.field_username_text : ""
      ),
      field_user_name_ms: decodeHTML(
        dataById ? dataById.payload.field_user_name_ms : editData ? editData?.[0]?.field_user_name_ms : ""
      ),
      field_user_name_zh: decodeHTML(
        dataById ? dataById.payload.field_user_name_zh : editData ? editData?.[0]?.field_user_name_zh : ""
      ),
      field_new_password_text: decodeHTML(
        dataById ? dataById.payload.field_new_password_text : editData ? editData?.[0]?.field_new_password_text : ""
      ),
      field_new_password_text_ms: decodeHTML(
        dataById
          ? dataById.payload.field_new_password_text_ms
          : editData
            ? editData?.[0]?.field_new_password_text_ms
            : ""
      ),
      field_new_password_text_zh: decodeHTML(
        dataById
          ? dataById.payload.field_new_password_text_zh
          : editData
            ? editData?.[0]?.field_new_password_text_zh
            : ""
      ),
      field_confirm_password: decodeHTML(
        dataById ? dataById.payload.field_confirm_password : editData ? editData?.[0]?.field_confirm_password : ""
      ),
      field_confirm_password_text_ms: decodeHTML(
        dataById
          ? dataById.payload.field_confirm_password_text_ms
          : editData
            ? editData?.[0]?.field_confirm_password_text_ms
            : ""
      ),
      field_confirm_password_zh: decodeHTML(
        dataById ? dataById.payload.field_confirm_password_zh : editData ? editData?.[0]?.field_confirm_password_zh : ""
      ),
      field_password_conditions: decodeHTML(
        dataById ? dataById.payload.field_password_conditions : editData ? editData?.[0]?.field_password_conditions : ""
      ),
      field_password_conditions_ms: decodeHTML(
        dataById
          ? dataById.payload.field_password_conditions_ms
          : editData
            ? editData?.[0]?.field_password_conditions_ms
            : ""
      ),
      field_password_conditions_zh: decodeHTML(
        dataById
          ? dataById.payload.field_password_conditions_zh
          : editData
            ? editData?.[0]?.field_password_conditions_zh
            : ""
      ),
      field_email_text: decodeHTML(
        dataById ? dataById.payload.field_email_text : editData ? editData?.[0]?.field_email_text : ""
      ),
      field_email_text_ms: decodeHTML(
        dataById ? dataById.payload.field_email_text_ms : editData ? editData?.[0]?.field_email_text_ms : ""
      ),
      field_email_text_zh: decodeHTML(
        dataById ? dataById.payload.field_email_text_zh : editData ? editData?.[0]?.field_email_text_zh : ""
      ),
      field_epc_id_text: decodeHTML(
        dataById ? dataById.payload.field_epc_id_text : editData ? editData?.[0]?.field_epc_id_text : ""
      ),
      field_epc_id_text_ms: decodeHTML(
        dataById ? dataById.payload.field_epc_id_text_ms : editData ? editData?.[0]?.field_epc_id_text_ms : ""
      ),
      field_epc_id_text_zh: decodeHTML(
        dataById ? dataById.payload.field_epc_id_text_zh : editData ? editData?.[0]?.field_epc_id_text_zh : ""
      ),
      field_update_text: decodeHTML(
        dataById ? dataById.payload.field_update_text : editData ? editData?.[0]?.field_update_text : ""
      ),
      field_update_ms: decodeHTML(
        dataById ? dataById.payload.field_update_ms : editData ? editData?.[0]?.field_update_ms : ""
      ),
      field_update_zh: decodeHTML(
        dataById ? dataById.payload.field_update_zh : editData ? editData?.[0]?.field_update_zh : ""
      ),
    },
    fields: fields.eramanUserProfile,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["field_icon_image", "field_icon_facebook", "field_eye_icon"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    // let descKeys: any = [
    //   "field_password_conditions_zh",
    //   "field_password_conditions_ms",
    //   "field_password_conditions",
    // ];
    // for (const key of descKeys) {
    //   try {
    //     data[key] = {
    //       value: data[key] || "",
    //       format: "full_html",
    //     };
    //   } catch (error) { }
    // }

    const payload = {
      action: action,
      method: method,
      module: "Contents",
      subModule: `eramanUserProfile`,
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: editData?.[0]?.id || dataById?.payload?.id }),
        ...data,
        published: true,
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <>
      <DynamicForm
        isLoading={isLoadingData || drilldownLoading}
        config={AwardsFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${eramanHost}/api/user-profile-page-data-crud-api/${editData?.[0]?.id || dataById?.payload?.id}`,
            "UPDATE",
            "PATCH",
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </>
  );
};
